import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bot, Terminal, Shield, AlertTriangle, CheckCircle2, Eye, Clock,
  ArrowLeft, Loader2, Target, Cpu, FileText, RefreshCw, Download,
  Copy, ChevronDown, ChevronRight, AlertOctagon, Wrench
} from 'lucide-react';
import { getPentestSession, exportPentestPdf, rescanPentestSession } from '@/services/litesabrePro';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
  critical: { badge: 'bg-red-600 text-white', border: 'border-l-red-500', bg: 'bg-red-50' },
  high: { badge: 'bg-orange-500 text-white', border: 'border-l-orange-500', bg: 'bg-orange-50' },
  medium: { badge: 'bg-yellow-500 text-black', border: 'border-l-yellow-500', bg: 'bg-yellow-50' },
  low: { badge: 'bg-blue-500 text-white', border: 'border-l-blue-500', bg: 'bg-blue-50' },
  info: { badge: 'bg-gray-400 text-white', border: 'border-l-gray-400', bg: 'bg-gray-50' },
};

const AIPentestSessionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/litesabre-pro') ? '/litesabre-pro' : '/grc/litesabre-pro';

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [rescanning, setRescanning] = useState(false);
  const pollRef = useRef(null);
  const logRef = useRef(null);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const data = await getPentestSession(sessionId);
        setSession(data);
        if (data.status !== 'running' && data.status !== 'pending') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        clearInterval(pollRef.current);
      }
    }, 3000);
  }, [sessionId]);

  const loadSession = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPentestSession(sessionId);
      setSession(data);
      if (data.findings?.length > 0) {
        setSelectedFinding(prev => prev || data.findings[0]);
      }
      if (data.status === 'running' || data.status === 'pending') {
        startPolling();
      }
    } catch (err) {
      toast.error('Failed to load pentest session');
    } finally {
      setLoading(false);
    }
  }, [sessionId, startPolling]);

  useEffect(() => {
    loadSession();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadSession]);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [session?.execution_log]);


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const downloadReport = () => {
    if (!session?.report) return;
    const blob = new Blob([session.report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pentest_report_${sessionId}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  const downloadPdf = async () => {
    try {
      const blob = await exportPentestPdf(sessionId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pentest_report_${session?.target || 'unknown'}_${sessionId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to export PDF');
    }
  };

  const handleRescan = async () => {
    try {
      setRescanning(true);
      const result = await rescanPentestSession(sessionId);
      toast.success(`Rescan started for ${result.target}`);
      navigate(`${basePath}/pentest/${result.new_session_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start rescan');
    } finally {
      setRescanning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Session not found</p>
        <Button className="mt-4" onClick={() => navigate(`${basePath}/dashboard`)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  const isRunning = session.status === 'running' || session.status === 'pending';
  const findings = session.findings || [];
  const filteredFindings = severityFilter === 'all'
    ? findings
    : findings.filter(f => f.severity === severityFilter);

  const severityCounts = {
    critical: findings.filter(f => f.severity === 'critical').length,
    high: findings.filter(f => f.severity === 'high').length,
    medium: findings.filter(f => f.severity === 'medium').length,
    low: findings.filter(f => f.severity === 'low').length,
  };

  return (
    <div className="p-6 space-y-6" data-testid="pentest-session-page">
      {/* Running Banner */}
      {isRunning && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          <div>
            <p className="font-medium text-blue-800">AI Agent Running</p>
            <p className="text-sm text-blue-600">The autonomous pentest agent is actively scanning. Results will update in real-time.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}/dashboard`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bot className="w-6 h-6 text-indigo-600" />
              {session.name}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
              <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {session.target}</span>
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> {session.scope}</span>
              <span className="flex items-center gap-1"><Cpu className="w-3 h-3" /> {session.ai_config?.provider}/{session.ai_config?.model}</span>
              <Badge className={
                session.status === 'completed' ? 'bg-emerald-600 text-white' :
                session.status === 'running' ? 'bg-blue-500 text-white animate-pulse' :
                session.status === 'failed' ? 'bg-red-600 text-white' : 'bg-yellow-500'
              }>
                {session.status}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isRunning && (
            <Button variant="outline" onClick={loadSession}>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Refresh
            </Button>
          )}
          {!isRunning && (
            <Button variant="outline" onClick={handleRescan} disabled={rescanning} data-testid="rescan-btn" className="text-indigo-600 border-indigo-200">
              {rescanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {rescanning ? 'Starting...' : 'Rescan'}
            </Button>
          )}
          {session.status === 'completed' && (
            <Button variant="outline" onClick={downloadPdf} data-testid="export-pdf-btn" className="text-indigo-600 border-indigo-200">
              <Download className="w-4 h-4 mr-2" /> Export PDF
            </Button>
          )}
          {session.report && (
            <Button variant="outline" onClick={downloadReport}>
              <Download className="w-4 h-4 mr-2" /> Download MD
            </Button>
          )}
        </div>
      </div>

      {/* Error Banner for Failed Sessions */}
      {session.status === 'failed' && session.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="session-error-banner">
          <AlertOctagon className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-red-800 text-sm">Pentest Failed</p>
            <p className="text-red-600 text-sm mt-0.5">{session.error}</p>
          </div>
        </div>
      )}

      {/* Multi-target display */}
      {session.targets?.length > 1 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="multi-target-banner">
          <p className="text-xs font-medium text-slate-600 mb-1.5">Targets ({session.targets.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {session.targets.map((t, i) => (
              <Badge key={i} variant="outline" className="text-xs font-mono">{t}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="bg-gray-900 text-white">
          <CardContent className="p-3">
            <p className="text-gray-400 text-xs">Total Findings</p>
            <p className="text-2xl font-bold">{findings.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-600 text-white">
          <CardContent className="p-3">
            <p className="text-red-200 text-xs">Critical</p>
            <p className="text-2xl font-bold">{severityCounts.critical}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-500 text-white">
          <CardContent className="p-3">
            <p className="text-orange-200 text-xs">High</p>
            <p className="text-2xl font-bold">{severityCounts.high}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-500">
          <CardContent className="p-3">
            <p className="text-yellow-800 text-xs">Medium</p>
            <p className="text-2xl font-bold">{severityCounts.medium}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-500 text-white">
          <CardContent className="p-3">
            <p className="text-blue-200 text-xs">Low</p>
            <p className="text-2xl font-bold">{severityCounts.low}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-gray-500 text-xs">Steps</p>
            <p className="text-2xl font-bold">{session.steps_taken || session.current_step || 0}<span className="text-sm text-gray-400">/{session.max_steps || 8}</span></p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview & Findings</TabsTrigger>
          <TabsTrigger value="execution-log">Execution Log</TabsTrigger>
          <TabsTrigger value="report">Report</TabsTrigger>
        </TabsList>

        {/* Overview & Findings */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Findings List */}
            <div className="lg:col-span-1">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Security Findings</CardTitle>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {['all', 'critical', 'high', 'medium', 'low'].map(sev => (
                      <Button
                        key={sev}
                        size="sm"
                        variant={severityFilter === sev ? 'default' : 'outline'}
                        className="text-xs h-7"
                        onClick={() => setSeverityFilter(sev)}
                        data-testid={`filter-${sev}`}
                      >
                        {sev === 'all' ? `All (${findings.length})` : `${sev} (${severityCounts[sev] || 0})`}
                      </Button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="findings-list">
                    {filteredFindings.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-8">
                        {isRunning ? 'Scanning in progress...' : 'No findings detected'}
                      </p>
                    )}
                    {filteredFindings.map((f) => {
                      const styles = SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.info;
                      return (
                        <div
                          key={f.id}
                          className={`border-l-4 ${styles.border} rounded-r p-3 cursor-pointer transition-all hover:shadow-md ${selectedFinding?.id === f.id ? 'ring-2 ring-indigo-400' : ''}`}
                          onClick={() => setSelectedFinding(f)}
                          data-testid={`finding-${f.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={styles.badge}>{f.severity}</Badge>
                            {f.confidence_score && (
                              <span className="text-xs text-gray-400">{f.confidence_score}%</span>
                            )}
                          </div>
                          <p className="text-sm font-medium line-clamp-2">{f.title}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Badge variant="outline" className="text-xs">{f.tool}</Badge>
                            <Badge variant="outline" className="text-xs">{f.category}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Finding Detail */}
            <div className="lg:col-span-2">
              {selectedFinding ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={SEVERITY_STYLES[selectedFinding.severity]?.badge}>
                            {selectedFinding.severity?.toUpperCase()}
                          </Badge>
                          {selectedFinding.confidence_score && (
                            <Badge variant="outline">{selectedFinding.confidence_score}% confidence</Badge>
                          )}
                          <Badge variant="outline">{selectedFinding.category}</Badge>
                        </div>
                        <CardTitle className="text-lg">{selectedFinding.title}</CardTitle>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(JSON.stringify(selectedFinding, null, 2))}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* OWASP Category & CVSS */}
                    {(selectedFinding.owasp_category || selectedFinding.cvss_range) && (
                      <div className="flex flex-wrap gap-2">
                        {selectedFinding.owasp_category && (
                          <Badge variant="secondary" className="text-xs">{selectedFinding.owasp_category}</Badge>
                        )}
                        {selectedFinding.cvss_range && (
                          <Badge variant="outline" className="text-xs">CVSS: {selectedFinding.cvss_range}</Badge>
                        )}
                        {selectedFinding.affected_target && (
                          <Badge variant="outline" className="text-xs font-mono">{selectedFinding.affected_target}</Badge>
                        )}
                      </div>
                    )}

                    {/* Overview / Description */}
                    <div>
                      <h4 className="font-semibold text-sm text-gray-500 mb-1">Overview</h4>
                      <p className="text-sm leading-relaxed">{selectedFinding.description}</p>
                    </div>

                    {/* Steps to Reproduce */}
                    {selectedFinding.steps_to_reproduce && (
                      <div>
                        <h4 className="font-semibold text-sm text-gray-500 mb-1">Steps to Reproduce</h4>
                        <pre className="bg-gray-50 border border-gray-200 text-gray-800 p-3 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                          {selectedFinding.steps_to_reproduce}
                        </pre>
                      </div>
                    )}

                    {/* Evidence / Proof of Concept */}
                    {selectedFinding.evidence && (
                      <div>
                        <h4 className="font-semibold text-sm text-gray-500 mb-1">Proof of Concept</h4>
                        <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">
                          {selectedFinding.evidence}
                        </pre>
                      </div>
                    )}

                    {/* Impact */}
                    {selectedFinding.impact_detail && (
                      <div>
                        <h4 className="font-semibold text-sm text-gray-500 mb-1">Impact</h4>
                        <div className="bg-red-50 border border-red-200 rounded p-3">
                          <p className="text-sm text-red-800">{selectedFinding.impact_detail}</p>
                        </div>
                      </div>
                    )}

                    {/* Remediation */}
                    {selectedFinding.remediation && (
                      <div>
                        <h4 className="font-semibold text-sm text-gray-500 mb-1 flex items-center gap-1">
                          <Wrench className="w-4 h-4" /> Remediation
                        </h4>
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-1">
                          <p className="text-sm text-emerald-800 font-medium">{selectedFinding.remediation}</p>
                          {selectedFinding.remediation_details?.length > 1 && (
                            <ul className="text-xs text-emerald-700 mt-2 space-y-1 pl-4 list-disc">
                              {selectedFinding.remediation_details.slice(1).map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div>
                        <span className="text-xs text-gray-400">Tool</span>
                        <p className="text-sm font-mono">{selectedFinding.tool}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Category</span>
                        <p className="text-sm">{selectedFinding.category}</p>
                      </div>
                      {selectedFinding.confidence_score && (
                        <div>
                          <span className="text-xs text-gray-400">Confidence</span>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${selectedFinding.confidence_score >= 80 ? 'bg-emerald-500' : selectedFinding.confidence_score >= 50 ? 'bg-amber-500' : 'bg-gray-400'}`}
                                style={{ width: `${selectedFinding.confidence_score}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold">{selectedFinding.confidence_score}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center text-gray-400">
                    <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>Select a finding to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Execution Log */}
        <TabsContent value="execution-log" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Terminal className="w-4 h-4" /> Agent Execution Log
                {isRunning && <Badge className="bg-blue-500 text-white text-xs animate-pulse ml-2">LIVE</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div ref={logRef} className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="execution-log">
                {(session.execution_log || []).map((entry, idx) => (
                  <ExecutionLogEntry key={idx} entry={entry} />
                ))}
                {isRunning && (
                  <div className="flex items-center gap-2 p-4 text-sm text-gray-400 border rounded">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Agent is thinking...
                  </div>
                )}
                {session.execution_log?.length === 0 && !isRunning && (
                  <p className="text-sm text-gray-400 text-center py-8">No execution log available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report */}
        <TabsContent value="report" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Penetration Test Report
                </CardTitle>
                {session.report && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(session.report)}>
                      <Copy className="w-4 h-4 mr-1" /> Copy
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadReport}>
                      <Download className="w-4 h-4 mr-1" /> Markdown
                    </Button>
                    {session.status === 'completed' && (
                      <Button size="sm" onClick={downloadPdf} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="report-export-pdf">
                        <Download className="w-4 h-4 mr-1" /> PDF Report
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {session.report ? (
                <div className="prose prose-sm max-w-none" data-testid="pentest-report">
                  <div className="bg-gray-50 rounded-lg p-6 whitespace-pre-wrap text-sm leading-relaxed">
                    {session.report}
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-400 py-12">
                  {isRunning ? (
                    <div>
                      <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />
                      <p>Report will be generated when the assessment completes</p>
                    </div>
                  ) : (
                    <div>
                      <FileText className="w-8 h-8 mx-auto mb-3 opacity-50" />
                      <p>No report generated</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ========== Execution Log Entry Component ==========

const ExecutionLogEntry = ({ entry }) => {
  const [expanded, setExpanded] = useState(entry.phase === 'observe' ? false : true);

  const getPhaseConfig = (phase) => {
    switch (phase) {
      case 'think': return { icon: <Bot className="w-4 h-4" />, color: 'border-l-indigo-500 bg-indigo-50/50', label: 'THINK', textColor: 'text-indigo-700' };
      case 'act': return { icon: <Terminal className="w-4 h-4" />, color: 'border-l-emerald-500 bg-emerald-50/50', label: 'ACT', textColor: 'text-emerald-700' };
      case 'observe': return { icon: <Eye className="w-4 h-4" />, color: 'border-l-amber-500 bg-amber-50/50', label: 'OBSERVE', textColor: 'text-amber-700' };
      case 'complete': return { icon: <CheckCircle2 className="w-4 h-4" />, color: 'border-l-green-500 bg-green-50/50', label: 'DONE', textColor: 'text-green-700' };
      case 'error': return { icon: <AlertOctagon className="w-4 h-4" />, color: 'border-l-red-500 bg-red-50/50', label: 'ERROR', textColor: 'text-red-700' };
      default: return { icon: <Clock className="w-4 h-4" />, color: 'border-l-gray-300 bg-gray-50/50', label: phase, textColor: 'text-gray-700' };
    }
  };

  const config = getPhaseConfig(entry.phase);

  return (
    <div className={`border-l-4 rounded-r p-3 ${config.color}`}>
      <div
        className="flex items-center gap-2 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {config.icon}
        <span className={`text-xs font-bold uppercase tracking-wider ${config.textColor}`}>
          Step {entry.step} - {config.label}
        </span>
        {entry.tool && entry.phase !== 'observe' && (
          <Badge variant="outline" className="font-mono text-xs">{entry.tool}</Badge>
        )}
        {entry.phase === 'observe' && (
          <Badge variant="outline" className="font-mono text-xs">{entry.tool} output</Badge>
        )}
        <span className="text-xs text-gray-300 ml-auto">
          {new Date(entry.timestamp).toLocaleTimeString()}
        </span>
        {expanded ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
      </div>

      {expanded && (
        <div className="mt-2">
          {entry.thought && (
            <p className="text-sm text-gray-700 mb-2">{entry.thought}</p>
          )}
          {entry.phase === 'observe' && entry.output && (
            <pre className="text-xs font-mono bg-gray-900 text-green-400 p-3 rounded overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
              {entry.output}
            </pre>
          )}
          {entry.phase === 'complete' && entry.report && (
            <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border">
              {entry.report}
            </div>
          )}
          {entry.error && (
            <p className="text-sm text-red-600 bg-red-50 rounded p-2">{entry.error}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AIPentestSessionPage;
